import React, { useEffect, useState, useCallback } from "react";
import AsyncSelect from "react-select/async";
import Select from "react-select";
import { debounce } from "lodash";

const SearchSelectBox = ({
  value,
  onChange,
  error,
  options = [],
  title = "Select an option",
  placeholder,
  disable = false,
  loading = false,
  isAsync = false,
  fetchOptions = () => Promise.resolve([]),
  defaultFetch = false,
  isUsingFilter = false,
  noMargin = false,
  className = "",
}) => {
  const [defaultOptions, setDefaultOptions] = useState([]);

  useEffect(() => {
    if (isAsync && defaultFetch && typeof fetchOptions === "function") {
      fetchOptions("")
        .then((data) => setDefaultOptions(data))
        .catch((error) =>
          console.error("Error fetching default options:", error)
        );
    }
  }, [isAsync, defaultFetch]);

  const loadOptions = debounce((inputValue, callback) => {
    if (typeof fetchOptions !== "function") {
      console.error("fetchOptions is not a function!");
      callback([]);
      return;
    }
    fetchOptions(inputValue)
      .then((results) => callback(results))
      .catch((error) => {
        console.error("Error fetching options:", error);
        callback([]);
      });
  }, 500);

  const handleChange = (selectedOption) => {
    onChange(selectedOption);
  };

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: state.isDisabled ? "#f1f3f5" : "white",
      borderColor: state.isDisabled ? "#d6d8db" : "lightgray",
      boxShadow: "none",
      cursor: state.isDisabled ? "not-allowed" : "default",
      color: state.isDisabled ? "#6c757d" : "black",
      "&:hover": {
        borderColor: state.isDisabled ? "#d6d8db" : "#3586f9",
      },
      width: isUsingFilter ? "200px" : "",
      minHeight: "30px",
      height: "30px",
      fontSize: "1.1rem",
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: "0px 8px",
      height: "30px",
    }),
    input: (provided) => ({
      ...provided,
      margin: "0px",
      padding: "0px",
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: "30px",
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: "white",
      color: "black",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused ? "#e2e8f0" : "white",
      color: "black",
      "&:active": { backgroundColor: "#3586f9", color: "white" },
      fontSize: "1.3rem",
    }),
    placeholder: (provided, state) => ({
      ...provided,
      color: state.isDisabled ? "#a0a0a0" : "gray",
      fontSize: "1.3rem",
    }),
    singleValue: (provided, state) => ({
      ...provided,
      color: state.isDisabled ? "#6c757d" : "black",
      fontSize: "1.3rem",
    }),
  };

  // console.log(defaultOptions.find((option) => option.value === value), value);

  const placeholderText = placeholder || title;

  return (
    <div className={`${isUsingFilter || noMargin ? "" : "mb-3 lg:mb-5"} ${className}`.trim()}>
      {isAsync ? (
        <AsyncSelect
          // cacheOptions
          defaultOptions={defaultOptions}
          loadOptions={loadOptions}
          onChange={handleChange}
          placeholder={loading ? "Loading..." : placeholderText}
          classNamePrefix="select"
          styles={customStyles}
          isDisabled={disable}
          isLoading={loading}
          isClearable={true}
          isSearchable={true}
          value={
            value && defaultOptions?.length
              ? typeof value === "object"
                ? value
                : defaultOptions.find((option) => option.value === value) || null
              : null
          }
        />
      ) : (
        <Select
          isSearchable={true}
          isClearable={true}
          options={options}
          onChange={handleChange}
          placeholder={loading ? "Loading..." : placeholderText}
          name="select-box"
          value={
            value && options?.length
              ? typeof value === "object"
                ? value
                : options.find((option) => option.value === value) || null
              : null
          }
          classNamePrefix="select"
          styles={customStyles}
          isDisabled={disable}
          isLoading={loading}
        />
      )}
      {error && <p className="form-text text-danger fs-5">{error}</p>}
    </div>
  );
};

export default SearchSelectBox;
